import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Drawer,
  Box,
  alertTitleClasses,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PhoneIcon from "@mui/icons-material/Phone";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { settings } from "../data/settings";
import { useNavigate } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import theme from "../theme";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { RDVButton } from "./commonWidgets";

// Barre de navigation avec couleur dynamique
const StyledAppBar = styled(AppBar)`
  height: ${settings.navbar.height} !important;
  background-color: ${({ $isScrolled }) =>
    $isScrolled ? "rgba(255, 255, 255, 0.7)" : "#ffffff"} !important;
  backdrop-filter: ${({ $isScrolled }) =>
    $isScrolled ? "blur(5px)" : "none"} !important;
  transition:
    background-color 0.3s ease,
    backdrop-filter 0.3s ease;
  box-shadow: ${({ $isScrolled }) =>
    $isScrolled ? "0 2px 6px rgba(0, 0, 0, 0.1)" : "none"};
  color: ${(props) => props.$fontColor || "black"} !important;
`;

// Conteneur flexible pour les sections
const LeftContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
  height: 100%;
`;

const CenterContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: ${(props) =>
    props.$position === "center" ? "center" : "flex-start"};
  height: 100%;
`;

const RightContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: flex-end;
  height: 100%;
`;

// Logo stylisé
const Logo = styled.div`
  ${(props) => (props.$isDrawer === true ? " width: 100%;" : "height: 60%;")}
  margin-left: ${(props) => (props.$position === "right" ? "auto" : "")};
  margin-right: ${(props) => (props.$position === "left" ? "auto" : "")};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.3rem 0;
  & img {
      
    height: 100%;
    max-height: ${(props) => (props.$isDrawer === true ? "3rem" : "100%")};
    padding: ${(props) => (props.$isDrawer === true ? " 0.5rem" : "0rem")};
 
`;

// Absolute centred logo container
const AbsoluteLogoWrapper = styled(Box)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  height: 100%;
`;

// Styles des éléments de menu
const StyledListItem = styled(ListItem)`
  text-transform: uppercase;
  color: ${(p) => (p.$submenu ? "rgba(24,33,69,.75)" : "var(--my-color3)")};
  text-decoration: none;
  white-space: nowrap;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  transition: color 0.3s ease;
  padding: 0rem 0.6rem !important;
  line-height: 1rem;
  margin: 0rem 0.7rem !important;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--my-color3);
    border-radius: 8px;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s ease;
    z-index: -1;
  }

  &:hover {
    color: #fff;
  }

  &:hover::after {
    transform: scaleX(1);
  }

  &.submenu {
    margin: 0rem 0.7rem 0rem 2rem !important; /* retrait à gauche */
    & span {
      font-size: 0.85rem !important;
    }
  }
`;
const StyledListItemText = styled(ListItemText)`
  font-weight: inherit !important;
  text-transform: inherit !important;
  color: inherit !important;
`;

const Navbar = ({ pages, settings }) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation(); // Récupérer la page actuelle
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // true si écran < md (mobile)

  const [firstKeyPage, setFirstKeyPage] = useState(
    pages && Object.keys(pages).length > 0 ? Object.keys(pages)[0] : "",
  );

  const [isScrolled, setIsScrolled] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const getOfferHref = (subKey, subData) =>
    subData?.href || `/offres/${subKey}`;
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10); // dès qu'on commence à scroller
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleDrawer = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    setFirstKeyPage(
      pages && Object.keys(pages).length > 0 ? Object.keys(pages)[0] : "",
    );
  }, [pages]);

  const handleScrollToId = (id) => {
    const isHomePage = location.pathname === "/";

    // Gestion spéciale pour les pages d'offres
    if (id === "offres") {
      if (!isHomePage) {
        navigate("/#offres");
      } else {
        const scrollToElement = () => {
          setTimeout(() => {
            const el = document.getElementById("offres");
            if (el) {
              const navbarHeight = parseFloat(
                settings.navbar?.height || "60px",
              );
              const yOffset = -navbarHeight;
              const y =
                el.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({ top: y, behavior: "smooth" });
            }
          }, 300);
        };
        scrollToElement();
      }
      setMobileOpen(false);
      return;
    }

    const scrollToElement = () => {
      setTimeout(() => {
        const el = document.getElementById(id);

        const getNavbarHeightInPixels = () => {
          const heightStr = settings.navbar?.height || "5rem";
          if (heightStr.endsWith("rem")) {
            const remValue = parseFloat(heightStr);
            const baseFontSize = parseFloat(
              getComputedStyle(document.documentElement).fontSize,
            ); // généralement 16px
            return remValue * baseFontSize;
          } else if (heightStr.endsWith("px")) {
            return parseFloat(heightStr);
          } else {
            return parseFloat(heightStr);
          }
        };

        if (el) {
          const navbarHeight = getNavbarHeightInPixels();
          const yOffset = -navbarHeight;
          const y =
            el.getBoundingClientRect().top + window.pageYOffset + yOffset;

          window.scrollTo({ top: y, behavior: "smooth" });
          setMobileOpen(false);
        }
      }, 300); // petit délai pour laisser React re-render la page
    };

    if (!isHomePage) {
      navigate(`/#${id}`);
      // écoute le prochain changement d'URL puis scroll
      const unlisten = window.addEventListener("popstate", () => {
        scrollToElement();
        window.removeEventListener("popstate", unlisten);
      });
    } else {
      scrollToElement();
    }
    setMobileOpen(false);
  };

  return (
    <>
      <StyledAppBar
        position="fixed"
        $bgColor={settings?.navbar?.backgroundColor}
        $fontColor={settings?.navbar?.fontColor}
        $isScrolled={isScrolled}
      >
        <Toolbar>
          {/* Zone Gauche */}
          <LeftContainer>
            {/* Menu burger en mobile */}
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer}
              sx={{ display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>

            {!isMobile && settings.navbar?.logoImg && (
              <Logo
                onClick={() => {
                  navigate("/");
                }}
              >
                {settings.navbar?.logoImg}
              </Logo>
            )}
            {/* Liens qui doivent être à gauche */}
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              {Object.keys(pages)
                .filter((key) => pages[key].position === "left")
                .map((key, index) => {
                  const page = pages[key];

                  if (page.sub) {
                    return (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          position: "relative",
                        }}
                      >
                        <StyledListItem
                          onClick={handleMenuOpen}
                          sx={{
                            color: "black",
                            textDecoration: "none",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <StyledListItemText
                            primary={page.title}
                            $active={
                              location.pathname === `/${key}` ||
                              (location.pathname === "/" &&
                                key === firstKeyPage)
                            }
                          />
                          <ArrowDropDownIcon />
                        </StyledListItem>

                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={handleMenuClose}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                        >
                          {Object.entries(page.sub).map(([subKey, subData]) => (
                            <MenuItem
                              key={subKey}
                              onClick={() => {
                                navigate(getOfferHref(subKey, subData));
                                handleMenuClose();
                              }}
                            >
                              <StyledListItemText
                                className="submenu"
                                primary={subData.title}
                              />
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    );
                  }
                  // Liens normaux
                  return (
                    <StyledListItem
                      key={key}
                      onClick={() => handleScrollToId(key)}
                      sx={{
                        color: "black",
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <StyledListItemText
                        primary={page.title}
                        $active={
                          location.pathname === `/${key}` ||
                          (location.pathname === "/" && key === firstKeyPage)
                        }
                      />
                    </StyledListItem>
                  );
                })}
            </Box>
          </LeftContainer>

          {/* Absolute centered logo */}
          {isMobile && settings.navbar?.logoImg && (
            <AbsoluteLogoWrapper>
              <Logo
                onClick={() => {
                  navigate("/");
                }}
              >
                {settings.navbar?.logoImg}
              </Logo>
            </AbsoluteLogoWrapper>
          )}

          {/* Zone Centre placeholder to keep flex layout */}
          <CenterContainer />

          {/* Zone Droite */}
          <RightContainer>
            {/* Liens qui doivent être à droite */}
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              {Object.keys(pages)
                .filter((key) => pages[key].position === "right")
                .map((key, index) => {
                  const page = pages[key];

                  if (page.sub) {
                    return (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          position: "relative",
                        }}
                      >
                        <StyledListItem
                          onClick={handleMenuOpen}
                          sx={{
                            color: "black",
                            textDecoration: "none",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <StyledListItemText
                            primary={page.title}
                            $active={
                              location.pathname === `/${key}` ||
                              (location.pathname === "/" &&
                                key === firstKeyPage)
                            }
                          />
                          <ArrowDropDownIcon />
                        </StyledListItem>

                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={handleMenuClose}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                        >
                          {Object.entries(page.sub).map(([subKey, subData]) => (
                            <MenuItem
                              key={subKey}
                              onClick={() => {
                                navigate(getOfferHref(subKey, subData));
                                handleMenuClose();
                              }}
                            >
                              <StyledListItemText
                                className="submenu"
                                primary={subData.title}
                              />
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    );
                  }
                  // Liens normaux
                  return (
                    <StyledListItem
                      key={key}
                      onClick={() => handleScrollToId(key)}
                      sx={{
                        color: "black",
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <StyledListItemText
                        primary={page.title}
                        $active={
                          location.pathname === `/${key}` ||
                          (location.pathname === "/" && key === firstKeyPage)
                        }
                      />
                    </StyledListItem>
                  );
                })}
            </Box>
            {/* CTA Icon mobile */}
            <Box sx={{ display: { xs: "block", md: "none" }, mr: 1 }}>
              <RDVButton onlyIcon={true} />
            </Box>

            {/* CTA Button desktop */}
            <Box sx={{ display: { xs: "none", md: "block" }, ml: 1 }}>
              <RDVButton label="Planifier un appel" />
            </Box>
          </RightContainer>
        </Toolbar>
      </StyledAppBar>

      {/* Drawer pour mobile */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={toggleDrawer}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 260,
            backgroundColor: "#ffffff",
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
            boxShadow: "0 8px 24px rgba(0,0,0,.15)",
            color: settings?.navbar?.fontColor || "black",
          },
        }}
      >
        <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer}>
          <Logo
            onClick={() => {
              navigate("/");
            }}
            $isDrawer={true}
          >
            {settings.navbar?.logoImg}
          </Logo>
          <List>
            {Object.keys(pages).map((key) => {
              const page = pages[key];
              return (
                <React.Fragment key={key}>
                  <ListItem
                    disablePadding
                    sx={{
                      "&:not(:last-child)": {
                        borderBottom: "1px solid rgba(0,0,0,.05)",
                      },
                    }}
                  >
                    <StyledListItem
                      onClick={() => handleScrollToId(key)}
                      sx={{ color: "black", textDecoration: "none" }}
                    >
                      <StyledListItemText primary={page.title} />
                    </StyledListItem>
                  </ListItem>
                  {page.sub &&
                    Object.entries(page.sub).map(([subKey, subData]) => (
                      <StyledListItem
                        className="submenu"
                        disablePadding
                        onClick={() => {
                          navigate(getOfferHref(subKey, subData));
                          setMobileOpen(false);
                        }}
                        sx={{
                          "&:not(:last-child)": {
                            borderBottom: "1px solid rgba(0,0,0,.05)",
                          },
                        }}
                      >
                        <StyledListItemText primary={subData.title} />
                      </StyledListItem>
                    ))}
                </React.Fragment>
              );
            })}
            {/* CTA inside drawer */}
            <ListItem sx={{ mt: 2, justifyContent: "center" }}>
              <RDVButton label="Planifier un appel" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
